import { memo, useMemo, useCallback } from 'react';
import { ChartWrapper } from '../shared/ChartWrapper';
import { TRAFFIC_HEX, sortByName } from '../shared/constants';
import { useOverviewData } from './useOverviewData';
import { AowCard } from './AowCard';

function HloBlock({ hlo, index, colors }) {
  const aligned = (hlo.indicators || []).filter(i => !i.isUnaligned);
  const withTarget = aligned.filter(i => i.achievementPct !== null);
  const noTarget = aligned.length - withTarget.length;
  const met = withTarget.filter(i => i.achievementPct >= 100).length;
  const total = withTarget.length;
  const barColor = TRAFFIC_HEX[hlo.rating] || TRAFFIC_HEX.unknown;
  const barWidth = hlo.achievementPct != null ? `${Math.min(Math.round(hlo.achievementPct), 100)}%` : '0%';

  return (
    <div className="py-2.5 border-b border-black/5 last:border-b-0">
      <div className="flex items-start gap-2">
        <span className="text-sm font-bold opacity-40 tabular-nums mt-0.5 w-5 text-right flex-shrink-0">
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <div>
            <p className="text-xs font-semibold text-gray-800 leading-snug">
              {hlo.shortTitle || 'Ungrouped'}
            </p>
            <span className="block text-[10px] text-gray-500 tabular-nums text-right mt-0.5">
              {total > 0 && <>{met}/{total} indicators met annual target</>}
              {noTarget > 0 && (
                <span className="text-gray-400">{total > 0 ? ' + ' : ''}{noTarget} no target</span>
              )}
            </span>
          </div>
          <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden mt-1">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: barWidth, backgroundColor: barColor }}
            />
          </div>

          {/* Child indicators — always visible */}
          {hlo.indicators && hlo.indicators.length > 0 && (
            <div className="mt-1.5 ml-1 border-l-2 pl-2.5" style={{ borderColor: colors.border + '40' }}>
              {hlo.indicators.map((ind, i) => {
                const indPct = ind.achievementPct != null ? Math.round(ind.achievementPct) : null;
                const indColor = TRAFFIC_HEX[ind.rating] || TRAFFIC_HEX.unknown;
                return (
                  <div key={i} className="py-1 border-b border-black/[0.03] last:border-b-0">
                    <div>
                      <p className="text-[11px] text-gray-600 leading-snug">
                        {ind.indicator || ind.type || `Indicator ${i + 1}`}
                      </p>
                      <div className="flex items-center justify-end gap-1.5 mt-0.5">
                        {ind.achievementPct !== null ? (
                          <>
                            <span className="text-[10px] text-gray-400 tabular-nums whitespace-nowrap">
                              {ind.achieved ?? 0} / {ind.target ?? 0}
                            </span>
                            <span
                              className="text-[10px] font-semibold tabular-nums whitespace-nowrap"
                              style={{ color: indColor }}
                            >
                              {indPct}%
                            </span>
                          </>
                        ) : (
                          <span className="text-[10px] text-gray-400 italic whitespace-nowrap">
                            No target set
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailResultBox({ title, items, colors }) {
  return (
    <div
      className="rounded-xl border-2 overflow-hidden flex-1 min-w-0"
      style={{ borderColor: colors.border, backgroundColor: colors.bg }}
    >
      <div className="px-3 py-2" style={{ backgroundColor: colors.headerBg }}>
        <h5 className="text-xs font-bold uppercase tracking-wider" style={{ color: colors.text }}>
          {title}
        </h5>
      </div>
      <div className="px-3 py-1">
        {items.map((hlo, i) => (
          <HloBlock key={hlo.shortTitle || i} hlo={hlo} index={i} colors={colors} />
        ))}
      </div>
    </div>
  );
}

/** Preview wrapper filtered to first AoW (for ChartSelectionModal hover). */
export function AowTocFrameworkDetailPreview() {
  const { areasOfWork } = useOverviewData();
  const first = areasOfWork.find(a => a.hlos?.length);
  if (!first) return null;
  return <AowTocFrameworkDetailDiagram aowFilter={first.id} />;
}

export const AowTocFrameworkDetailDiagram = memo(function AowTocFrameworkDetailDiagram({ aowFilter }) {
  const { areasOfWork: rawAreasOfWork } = useOverviewData();

  const areasOfWork = useMemo(
    () => [...rawAreasOfWork].sort(sortByName),
    [rawAreasOfWork]
  );

  const displayAows = useMemo(
    () => aowFilter ? areasOfWork.filter(a => a.id === aowFilter) : areasOfWork,
    [areasOfWork, aowFilter]
  );

  const chartData = useMemo(() => {
    if (!displayAows.length) return [];
    return displayAows.flatMap(aow =>
      (aow.hlos || []).flatMap(hlo =>
        (hlo.indicators || []).map(ind => ({
          area_of_work: aow.name,
          aow_rating: aow.rating,
          result_type: hlo.resultType || 'Untyped',
          hlo_short_title: hlo.shortTitle,
          indicator: ind.indicator || ind.type || '',
          target: ind.target,
          achieved: ind.achieved,
          achievement_pct: ind.achievementPct != null ? Math.round(ind.achievementPct) : null,
          rating: ind.rating,
        }))
      )
    );
  }, [displayAows]);

  const renderBox = useCallback((title, items, colors) => (
    <DetailResultBox title={title} items={items} colors={colors} />
  ), []);

  if (!displayAows.length) {
    return (
      <ChartWrapper
        title="ToC Framework Detail — All Indicators"
        info="Displays each Area of Work with all indicators fully expanded under their High Level Outputs and Intermediate Outcomes."
        chartData={[]}
        exportFilename="aow-toc-framework-detail"
      >
        <p className="text-sm text-[var(--text-tertiary,#9ca3af)] italic text-center py-8">
          Select a specific program to view the detailed ToC Framework.
        </p>
      </ChartWrapper>
    );
  }

  return (
    <ChartWrapper
      title="ToC Framework Detail — All Indicators"
      info="Displays each Area of Work with all indicators fully expanded under their High Level Outputs and Intermediate Outcomes, with achieved vs target values."
      chartData={chartData}
      exportFilename="aow-toc-framework-detail"
    >
      <div className="space-y-5">
        {displayAows.map(aow => (
          <AowCard key={aow.id} aow={aow} renderBox={renderBox} />
        ))}
      </div>
    </ChartWrapper>
  );
});
